import { Badge, Button, EmptyState, LoadingRows } from '@shared/ui';
import { Fragment, useState } from 'react';

import { previewSummary, recordCells } from '../model/columns';
import { moduleReportView } from '../model/module-report';
import { outcomeReason } from '../model/outcome-copy';
import { recordIssueLines, rowsTableShape, rowsTableSpan } from '../model/rows-table';
import { issuesText, recordIssues, runStatusText, runTotalsText } from '../model/run-summary';
import { isSelectable, isSelected, type Selection } from '../model/selection';

import { OutcomeBadge } from './OutcomeBadge';
import { Td, Th } from './table';

import type { BulkModule, RecordRow, TenantRow } from '../schemas/bulk-migration';

/**
 * One row per client of a job. Two layouts over one table (`rowsTableShape`):
 *
 * - **analysis** — what would be migrated: the module's own columns (from the
 *   first record; the rest on expand), the Branding preview when the module has
 *   one, and a checkbox on every migratable client;
 * - **migration** — what happened: the run's status, its totals and the
 *   warnings and failures on the records the row carries. A module that opts in
 *   (`launchColumns`, Company Information) also shows its columns and expands
 *   each record with its warnings, next to the values a warning points at.
 */
export function TenantRowsTable({
    module,
    layout,
    rows,
    isLoading,
    hasMore,
    loadingMore,
    onLoadMore,
    selection,
    onToggle,
}: {
    module: BulkModule;
    layout: 'analysis' | 'migration';
    rows: readonly TenantRow[];
    isLoading: boolean;
    hasMore: boolean;
    loadingMore: boolean;
    onLoadMore: () => void;
    selection?: Selection | undefined;
    onToggle?: ((row: TenantRow) => void) | undefined;
}) {
    const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());
    const selectable = selection !== undefined && onToggle !== undefined;
    const shape = rowsTableShape(module, layout, moduleReportView(module.moduleId).launchColumns);
    const { columns, preview } = shape;
    const span = rowsTableSpan(shape, selectable);

    if (isLoading) return <LoadingRows label="Loading clients…" />;
    if (rows.length === 0) {
        return <EmptyState title="No clients here" description="Try another outcome or search." />;
    }

    const flip = (tenantId: string) => {
        setExpanded((current) => {
            const next = new Set(current);
            if (next.has(tenantId)) next.delete(tenantId);
            else next.add(tenantId);
            return next;
        });
    };

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[900px] border-collapse">
                <thead>
                    <tr>
                        {selectable && <Th className="w-10" />}
                        <Th>Client</Th>
                        <Th>Outcome</Th>
                        {columns.map((column) => (
                            <Th
                                key={column.key}
                                className={column.align === 'right' ? 'text-right' : undefined}
                            >
                                {column.label}
                            </Th>
                        ))}
                        {preview && <Th>Preview</Th>}
                        {shape.trailing.map((header) => (
                            <Th key={header}>{header}</Th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => {
                        const id = row.tenant.tenantId;
                        const first = row.records[0];
                        const isOpen = expanded.has(id);
                        const issues = recordIssues(row);
                        return (
                            <Fragment key={id}>
                                <tr className="hover:bg-surface-sunken">
                                    {selectable && (
                                        <Td>
                                            <input
                                                type="checkbox"
                                                aria-label={`Select ${row.tenant.displayName}`}
                                                checked={isSelected(selection, row)}
                                                disabled={!isSelectable(row)}
                                                onChange={() => {
                                                    onToggle(row);
                                                }}
                                                className="size-4 accent-brand"
                                            />
                                        </Td>
                                    )}
                                    <Td>
                                        <div className="flex items-center gap-2 font-semibold text-content">
                                            {row.tenant.displayName}
                                            {row.tenant.isSandbox && (
                                                <Badge tone="dev">Sandbox</Badge>
                                            )}
                                        </div>
                                        <div className="mt-0.5 font-mono text-[11.5px] text-content-faint">
                                            {row.tenant.slug ?? row.tenant.tenantId}
                                        </div>
                                    </Td>
                                    <Td>
                                        <OutcomeBadge outcome={row.outcome} />
                                        {outcomeReason(row) && (
                                            <div className="mt-1 max-w-[280px] text-[12px] text-content-muted">
                                                {outcomeReason(row)}
                                            </div>
                                        )}
                                    </Td>
                                    {columns.map((column) => (
                                        <Td
                                            key={column.key}
                                            className={
                                                column.align === 'right' ? 'text-right' : undefined
                                            }
                                        >
                                            {first ? recordCells([column], first)[0]?.text : '—'}
                                        </Td>
                                    ))}
                                    {preview && (
                                        <Td className="text-[12px] text-content-muted">
                                            {(first && previewSummary(first.preview)) ?? '—'}
                                        </Td>
                                    )}
                                    {layout === 'migration' && (
                                        <>
                                            <Td>{runStatusText(row.run)}</Td>
                                            <Td className="text-[12px] text-content-muted">
                                                {runTotalsText(row.run) || '—'}
                                            </Td>
                                            <Td className="text-[12px]">
                                                {issuesText(issues) ?? '—'}
                                            </Td>
                                        </>
                                    )}
                                    {shape.expandable && (
                                        <Td>
                                            {row.recordsTotal > 0 ? (
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        flip(id);
                                                    }}
                                                >
                                                    {isOpen ? 'Hide' : 'Show'}{' '}
                                                    {row.recordsTotal.toLocaleString('en-US')}
                                                </Button>
                                            ) : (
                                                '—'
                                            )}
                                        </Td>
                                    )}
                                </tr>
                                {shape.expandable && isOpen && (
                                    <tr>
                                        <td
                                            colSpan={span}
                                            className="border-b border-line-subtle bg-surface-sunken px-4 py-3"
                                        >
                                            <RecordList module={module} row={row} />
                                        </td>
                                    </tr>
                                )}
                            </Fragment>
                        );
                    })}
                </tbody>
            </table>
            {hasMore && (
                <div className="flex justify-center px-4 py-3">
                    <Button variant="ghost" size="sm" onClick={onLoadMore} disabled={loadingMore}>
                        {loadingMore ? 'Loading…' : 'Load more clients'}
                    </Button>
                </div>
            )}
        </div>
    );
}

/** Every record the row carries — at most 100; `recordsTotal` says how many exist. */
function RecordList({ module, row }: { module: BulkModule; row: TenantRow }) {
    return (
        <div className="flex flex-col gap-2">
            {row.records.map((record) => (
                <RecordLine key={record.sourceId} module={module} record={record} />
            ))}
            {row.recordsTotal > row.records.length && (
                <p className="text-[12px] text-content-muted">
                    Showing {row.records.length} of {row.recordsTotal.toLocaleString('en-US')}{' '}
                    records. Download the JSON for the runner&apos;s full report.
                </p>
            )}
        </div>
    );
}

function RecordLine({ module, record }: { module: BulkModule; record: RecordRow }) {
    const preview = module.supportsPreview ? previewSummary(record.preview) : null;
    return (
        <div className="text-[12.5px]">
            <span className="font-semibold text-content">{record.title}</span>{' '}
            <span className="font-mono text-[11px] text-content-faint">{record.sourceId}</span>
            <span className="ml-2 text-content-muted">
                {recordCells(module.columns, record)
                    .map((cell) => `${cell.label}: ${cell.text}`)
                    .join(' · ')}
                {preview ? ` · ${preview}` : ''}
            </span>
            {record.notMigrated && (
                <span className="ml-2 text-warning-text">
                    Not migrated — {record.notMigrated.message} ({record.notMigrated.code})
                </span>
            )}
            {recordIssueLines(record).map((line, index) => (
                <div key={index} className="mt-0.5 text-warning-text">
                    {line}
                </div>
            ))}
        </div>
    );
}
